import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Tracks whether the edited `value` has unsaved local changes relative to the last save.
 *
 * Mirrors {@link useAutoSave}'s baseline semantics so the two stay in lockstep: the first value seen
 * for a given `key` is the baseline (a freshly-loaded flow is NOT dirty), and changing `key` (e.g.
 * loading a different flow, or the same flow re-populating after load) re-baselines without flagging
 * dirty. Comparison is by reference, the builder's reducer produces new `screens` objects on every
 * edit, and a save does not mutate `screens`, so reference equality cleanly separates "edited" from
 * "just saved".
 *
 * Call `markClean()` right after a successful save (auto or manual) to re-baseline to the current
 * value, so the widget drops out of the "Unsaved changes" state.
 *
 * @param {*} key Identity of the thing being edited (e.g. `${flowId}:${loadGeneration}`)
 * @param {*} value The value to watch (e.g. the builder's screens)
 * @returns {{dirty: boolean, markClean: function}}
 */
export function useDirtyState(key, value) {
  const [dirty, setDirty] = useState(false);
  const baselineKeyRef = useRef(null);
  const baselineValueRef = useRef(null);
  const latestValueRef = useRef(value);
  latestValueRef.current = value;

  useEffect(() => {
    if (key == null) {
      return;
    }
    if (baselineKeyRef.current !== key) {
      baselineKeyRef.current = key;
      baselineValueRef.current = value;
      setDirty(false);
      return;
    }
    setDirty(value !== baselineValueRef.current);
  }, [key, value]);

  const markClean = useCallback(() => {
    baselineValueRef.current = latestValueRef.current;
    setDirty(false);
  }, []);

  return { dirty, markClean };
}
